import { Button, H2 } from "./primitives";
import { color, radius, shadow, space, type } from "@/lib/design-tokens";
import { about } from "@/lib/copy/about";
import { cta, shop } from "@/lib/copy/shop";

/**
 * The shop's map and visit card, shared by About and both footer variants.
 *
 * The keyless Google embed stays lazy-loaded. Live tiles are the only region
 * masked by the design-diff harness because they cannot match between runs.
 */
export function LocationBlock({
  id,
  dataSection,
}: {
  id?: string;
  dataSection?: string;
}) {
  return (
    <section id={id} data-section={dataSection} data-location-block="">
      <div
        data-map=""
        style={{
          position: "relative",
          overflow: "hidden",
          background: color.surface,
          minHeight: "clamp(420px,48vw,600px)",
          display: "flex",
          alignItems: "center",
          padding: space.gutter,
        }}
      >
        <div data-map-inner="" style={{ position: "absolute", inset: 0 }}>
          <iframe
            title={`Map to ${shop.name}, ${shop.addressLine1}, Boulder`}
            src="https://www.google.com/maps?q=Front+Range+Cargo+Bikes,+2500+47th+St,+Boulder,+CO&z=14&output=embed"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            style={{ width: "100%", height: "100%", border: 0, display: "block", filter: "saturate(.92)" }}
          />
        </div>
        <div
          data-reveal
          data-visit-card=""
          style={{
            position: "relative",
            background: color.white,
            borderRadius: radius.panel,
            boxShadow: shadow.visitCard,
            padding: "clamp(24px,3vw,36px)",
            maxWidth: "400px",
            width: "100%",
            minWidth: 0,
            boxSizing: "border-box",
          }}
        >
          <H2 style={{ fontSize: type.h3 }}>{about.visit.heading}</H2>
          <p style={{ margin: "12px 0 0", fontSize: "15.5px", lineHeight: 1.55, color: color.inkMuted }}>
            {about.visit.body}
          </p>
          <div style={{ margin: "20px 0 0", fontSize: "16px", lineHeight: 1.8 }}>
            <div style={{ fontWeight: 700 }}>{shop.hoursOpen}</div>
            <div style={{ color: color.red, fontWeight: 500 }}>{shop.hoursClosed}</div>
            <div style={{ marginTop: "10px" }}>
              <a
                href={shop.mapHref}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${shop.addressLine1}, ${shop.addressLine2} — get directions in Google Maps`}
                className="bb-underline-grow"
                style={{ color: "inherit", display: "inline-block" }}
              >
                {shop.addressLine1}
                <br />
                {shop.addressLine2}
              </a>
            </div>
            <div style={{ marginTop: "10px" }}>
              <a href={shop.phoneHref} className="bb-underline-grow" style={{ fontWeight: 700 }}>
                {shop.phone}
              </a>
              <br />
              <a href={shop.emailHref} className="bb-underline-grow" style={{ overflowWrap: "anywhere" }}>
                {shop.email}
              </a>
            </div>
          </div>
          <Button
            href={cta.testRideHref}
            variant="red"
            style={{ marginTop: "22px", fontSize: "15px", padding: "15px 24px" }}
          >
            {about.visit.cta}
          </Button>
          <div style={{ marginTop: "10px", fontSize: "13px", color: color.slate }}>{about.visit.note}</div>
        </div>
      </div>
    </section>
  );
}
